"use client"

import { useState } from "react"
import UploadIcon from "../../../assets/UploadIcon"
import SendInviteIcon from "../../../assets/SendInviteIcon"
import { Switch } from "../../Catalyst/switch"
import { useSelector } from "react-redux"
import { RootState } from "../../../store/store"
import ErrorIcon from "../../../assets/ErrorIcon"
import FileTypeIcon from "../../../assets/FileTypeIcon"
import DeleteIcon from "../../../assets/DeleteIcon"
import { uploadBulkUsers, checkUploadStatus } from "../../../services/UsersService"
import { useOrgId } from "../../../hooks/useOrgId"
import { useToast } from "../../../contexts/ToastContext"
import ConfirmUsersRemovalModal from "./ConfirmUsersRemovalModal"

export function UploadCSVComponent() {
  const [usersToRemove, setUsersToRemove] = useState<any[]>([]);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true)
  const [isDragging, setIsDragging] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [validationError, setValidationError] = useState("")
  const [isValidated, setIsValidated] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [rowCount, setRowCount] = useState(0)
  const [jobId, setJobId] = useState<string | null>(null)
  const [uploadStatus, setUploadStatus] = useState<string>("")
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const orgId = useOrgId()
  const { showSuccessToast, showErrorToast } = useToast()

  const orgDetails = useSelector(
    (state: RootState) => state.OrganizationListStore?.selectedOrgDetails
  )

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile?.name.endsWith(".csv")) {
      await handleValidatedFile(selectedFile)
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFile = e.dataTransfer.files?.[0]
    if (droppedFile?.name.endsWith(".csv")) {
      await handleValidatedFile(droppedFile)
    }
  }

  const handleValidatedFile = async (file: File) => {
    const result = await validateCSV(file)
    if (result.valid && result.rowCount) {
      setFile(file)
      setRowCount(result.rowCount)
      setValidationError("")
      setIsValidated(true)
    } else {
      setFile(null)
      setRowCount(0)
      setValidationError(result.error ?? "Invalid CSV format")
      setIsValidated(false)
    }
  }

  const handleUpload = async () => {
    if (!file || !orgId) return
    try {
      setUploading(true)
      setProgress(0)
      
      // Upload file
      const uploadResponse = await uploadBulkUsers(file, orgId, sendWelcomeEmail)
      const { job_id } = uploadResponse
      setJobId(job_id)
      
      // Start polling for status
      pollUploadStatus(job_id)
      
    } catch (error: any) {
      const status = error?.response?.status || error?.status;
      const data = error?.response?.data;

      if (status === 400 && data?.users_to_remove && data.users_to_remove.length > 0) {
        setShowConfirmModal(true);
        setUsersToRemove(data.users_to_remove);
      } else {
        let errorMessage = "Failed to upload file. Please try again.";
        if (data?.details && Array.isArray(data.details)) {
          errorMessage = data.details.join(', ');
        } else if (data?.message) {
          errorMessage = data.message;
        } else if (data?.error) {
          errorMessage = data.error;
        }
        showErrorToast('Upload failed', errorMessage);
      }
      setUploading(false)
    }
  }

  const pollUploadStatus = async (jobId: string) => {
    const maxPollingTime = 3 * 60 * 1000 // 3 minutes
    const pollingInterval = 500 // 500ms
    const startTime = Date.now()
    
    const poll = async () => {
      try {
        const statusResponse = await checkUploadStatus(jobId)
        const { status, processed, totalRecords, failed_records } = statusResponse
        
        setUploadStatus(status)
        
        // Calculate progress based on processed records
        if (totalRecords > 0) {
          const progressPercentage = Math.min((processed / totalRecords) * 100, 100)
          setProgress(progressPercentage)
        }
        
        if (status === 'COMPLETED' || status === 'FAILED') {
          setUploading(false)
          
          if (status === 'COMPLETED') {
            const successfullyProcessed = processed - (failed_records || 0)
            
            if (successfullyProcessed === 0) {
              // Show error toast if no users were successfully processed
              showErrorToast('Upload failed', 'There is an error processing your file. Please contact support.')
            } else {
              // Show success toast for successfully processed users
              let successMessage = ''
              if (sendWelcomeEmail) {
                successMessage = `Great! We've sent welcome emails to all new team members with instructions to get started. You can track their activation status in your dashboard.`
              } else {
                successMessage = `Your team members have been added to the platform. You can send welcome invites anytime from your dashboard or share access details directly with them.`
              }
              
              // Add failed records message if there are any
              if (failed_records && failed_records > 0) {
                successMessage += ` Please note system couldn't process ${failed_records} records.`
              }
              
              showSuccessToast(`(${successfullyProcessed}) new users successfully added`, successMessage)
            }
          } else {
            showErrorToast('Upload failed', 'There is an error processing your file. Please contact support.')
          }
          
          // Reset the component state for next upload
          setFile(null)
          setValidationError("")
          setIsValidated(false)
          setProgress(0)
          setRowCount(0)
          setJobId(null)
          setUploadStatus("")
          
          return
        }
        
        // Continue polling if not completed and within time limit
        if (Date.now() - startTime < maxPollingTime) {
          setTimeout(poll, pollingInterval)
        } else {
          // Timeout
          setUploading(false)
          showErrorToast('Upload timeout', 'Upload is taking longer than expected. Please check the status later.')
        }
        
      } catch (error) {
        console.log(error)
        setUploading(false)
        showErrorToast('Status check failed', 'Failed to check upload status.')
      }
    }
    
    // Start polling
    setTimeout(poll, pollingInterval)
  }

 
  const validateCSV = async (
  file: File
): Promise<{ valid: boolean; rowCount?: number; error?: string }> => {
  const expectedHeaders = ["first", "last", "email"]; // Header titles

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const rows = text.split("\n").map((r) => r.trim()).filter(Boolean);

        if (rows.length < 2) {
          return resolve({ valid: false, error: "CSV must contain a header and at least one user row." });
        }

        // Validate header row
        const headers = rows[0].split(",").map(h => h.trim().toLowerCase());
        if (headers.length !== expectedHeaders.length ||
          !expectedHeaders.every((h, idx) => headers[idx] === h)) {
          return resolve({ valid: false, error: `Headers must be exactly: ${expectedHeaders.join(", ")}` });
        }

        // Validate each user row
        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].split(",").map(v => v.trim());
          if (values.length < 3) {
            return resolve({ valid: false, error: `Row ${i + 1} must have 3 columns (first, last, email)` });
          }
          if (!values[0]) {
            return resolve({ valid: false, error: `Missing first name in row ${i + 1}` });
          }
          if (!values[1]) {
            return resolve({ valid: false, error: `Missing last name in row ${i + 1}` });
          }
          if (!values[2]) {
            return resolve({ valid: false, error: `Missing email in row ${i + 1}` });
          }
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(values[2])) {
            return resolve({ valid: false, error: `Invalid email format in row ${i + 1}: ${values[2]}` });
          }
        }
        resolve({ valid: true, rowCount: rows.length - 1 });
      };
      reader.readAsText(file);
    });
  };

  const onRemoveConfirmed = async () => {
    if (!file || !orgId) return;
    setShowConfirmModal(false);
    try {
      setUploading(true);
      setProgress(0);
      const uploadResponse = await uploadBulkUsers(file, orgId, sendWelcomeEmail, true);
      const { job_id } = uploadResponse;
      setJobId(job_id);
      pollUploadStatus(job_id);
    } catch (error: any) {
      setUploading(false);
      const errorMessage = error?.response?.data?.message || "Failed to remove users. Please try again.";
      showErrorToast('Remove failed', errorMessage);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h3 className="font-['Inter'] text-[14px] font-medium leading-[20px] text-[#525252] mb-1">Upload your CSV</h3>
      <p className="font-['Inter'] text-[12px] font-medium leading-[20px] text-[#525252] mb-3">
        Send invites to users in bulk. Your file must include column headers: 'first', 'last', and 'email'.
      </p>

      <button
        className={`w-full mb-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 p-10 transition ${isDragging ? "border-green-500 bg-green-50" : ""} ${validationError ? "border-red-500" : "border-gray-200 bg-white"}`}
        tabIndex={0}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            document.getElementById('csv-upload')?.click();
          }
        }}
      >
        {!file ? (
          <label htmlFor="csv-upload" className="flex cursor-pointer flex-col items-center">
            <div className="mb-4 shadow-light-sm p-2 border border-shadowborder rounded-lg">
              <UploadIcon />
            </div>
            <input id="csv-upload" type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
            <p className="mb-1 text8">
              <span className="text-green-500 desktext13">Click to upload</span> or drag and drop
            </p>
            <p className="text8 text-gray-500">CSV</p>
          </label>
        ) : (
          <div className="w-full">
            <div className="w-full flex justify-between items-center">
              <div className="flex items-center gap-3">
                {/* <div className="bg-green-600 text-white text-xs font-semibold px-2 py-0.5 rounded">CSV</div> */}
                <FileTypeIcon />
                <div className="flex flex-col">
                  <div className="desktext15 text-textColor">{file.name}</div>
                  <div className="desktext15 text-textColor3">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              </div>
              <button
                className="text-sm text-gray-500 hover:text-red-500"
                onClick={() => {
                  setFile(null)
                  setValidationError("")
                  setIsValidated(false)
                  setProgress(0)
                  setRowCount(0)
                  setUploading(false)
                  setJobId(null)
                  setUploadStatus("")
                }}
                aria-label="Remove uploaded file"
              >
                <DeleteIcon />
              </button>
            </div>
            {file && uploading && (
              <div className="mb-4 w-full h-2 mt-5 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-green-500 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            )}
          </div>

        )}
      </button>

      {validationError && (
        <div className="mb-4 -mt-2 flex items-center justify-center text-sm text-red-600">
          <div className="mr-3"><ErrorIcon /></div>
          There was an issue uploading your CSV. {validationError}
        </div>
      )}


      <div className="flex items-center justify-end gap-3">
        <div className="flex items-start gap-2">
          <Switch
            color="green"
            checked={sendWelcomeEmail}
            onChange={setSendWelcomeEmail}
          />
          <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#344054]">Send Welcome Series</span>
        </div>
        <div className={`${!isValidated || uploading ? 'opacity-50' : ''}`}>
          <button
            onClick={handleUpload}
            disabled={!isValidated || uploading}
            className="bg-white border border-neutral-200 rounded-lg shadow-sm px-3.5 py-2.5 flex items-center gap-1 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed"
          >
            <SendInviteIcon />
            <span className="font-['Inter'] font-medium text-[14px] leading-[20px] text-neutral-900">
              {rowCount > 0 ? (
                uploading ? 'Uploading...' : `Add (${rowCount}) Employees${sendWelcomeEmail ? " and Send Emails" : ""}`
              ) : (
                "Send Bulk Invitation"
              )}
            </span>
          </button>
        </div>
      </div>
      <ConfirmUsersRemovalModal
        isOpen={showConfirmModal}
        users={usersToRemove}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {onRemoveConfirmed()}}
      />
    </div>
  )
}
